// ©2024 Austin App House. All rights reserved.
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import InputRange from '../dashboard/inputRange';
import UserList from '../dashboard/userList';
import { toggleModal, toggleSecondModalClose } from '../../storage/actions/modalsActions';
import {
  fetchNearByContractors,
  setCurrentApplicants,
  sendInviteFromWorkOrderToContractor,
  sendInvitesFromWorkOrderToContractors,
} from '../../storage/actions/workOrdersActions';
import Loader from '../dashboard/common/Loader';
import { reloadData } from '../../storage/actions/applicationStateActions';
import getContractorGroups from './../../utils/api/get/getContractorGroups';
import Select from '../layout/CustomSelect';
import {
  dashboard as dashboardSelectStyle,
  dashboardSelectClassNames,
} from '../../utils/customSelectStyle';

const NearByContractorModal = ({
  modalState,
  toggleModal,
  toggleSecondModalClose,
  currentWorkOrder,
  availableApplicants,
  fetchNearByContractors,
  availableApplicantsLoaded,
  sendInviteFromWorkOrderToContractor,
  sendInvitesFromWorkOrderToContractors,
  routedApplicants,
  reloadData,
  userType,
}) => {
  const viewOnly = userType === 'superAdmin';
  const [range, changeRange] = useState(50);
  const [isFilteredByWorkedBefore, setIsFilteredByWorkedBefore] = useState(false);
  const [invitedContractors, setInvitedContractors] = useState([]);
  const [preferredGroups, setPreferredGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState(new Set());
  const [contractorList, setContractorList] = useState(availableApplicants || []);
  const [fullSelectCount, setFullSelectCount] = useState(0);

  const workOrderId = currentWorkOrder?._id;
  const isModalOpen = Boolean(modalState?.isOpen);
  const isRemoteWorkOrder = Boolean(currentWorkOrder?.isRemote);
  const hasSiteCoords = (currentWorkOrder?.coords?.coordinates?.length ?? 0) >= 2;

  const resetSelect = useCallback(() => setSelectedContractors(new Set()), []);

  const getAvailableToInviteContractors = useCallback((allContractors) => allContractors.filter((user) => {
    const isBlocked = Object.values(user.block).some((el) => el);
    const isInvited = invitedContractors.includes(user.userId);
    return !(isInvited || isBlocked);
  }), [invitedContractors]);

  const updateContractorList = useCallback(() => {
    if (!availableApplicants) {
      return;
    }

    const filteredUsers = availableApplicants.filter((user) => {
      if (isFilteredByWorkedBefore && !selectedGroups.length) {
        return user.completedWorkOrderCount > 0;
      }
      if (isFilteredByWorkedBefore && selectedGroups.length) {
        return user.completedWorkOrderCount > 0
          && user.groups.some((userGroup) => selectedGroups.find(
            (selectedGroup) => selectedGroup.value === userGroup.groupId,
          ));
      }
      if (!isFilteredByWorkedBefore && selectedGroups.length) {
        return user.groups.some((userGroup) => selectedGroups.find(
          (selectedGroup) => selectedGroup.value === userGroup.groupId,
        ));
      }
      return true;
    });

    setContractorList(filteredUsers);
    const availableToInvite = getAvailableToInviteContractors(filteredUsers);
    setFullSelectCount(availableToInvite.length);
    resetSelect();
  }, [
    availableApplicants,
    getAvailableToInviteContractors,
    isFilteredByWorkedBefore,
    resetSelect,
    selectedGroups,
  ]);

  useEffect(() => {
    getContractorGroups().then((groups) => {
      setPreferredGroups([
        { label: 'All groups', value: 'all' },
        ...groups.map((item) => ({ label: item.name, value: item._id })),
      ]);
    });
  }, []);

  useEffect(() => {
    if (!isModalOpen || !workOrderId) {
      return undefined;
    }

    if (!isRemoteWorkOrder && !hasSiteCoords) {
      return undefined;
    }

    fetchNearByContractors(workOrderId, range);
    resetSelect();
    return undefined;
  }, [
    fetchNearByContractors,
    hasSiteCoords,
    isModalOpen,
    isRemoteWorkOrder,
    range,
    resetSelect,
    workOrderId,
  ]);

  useEffect(() => {
    setInvitedContractors(routedApplicants?.data?.map((item) => item.contractor_id) || []);
  }, [routedApplicants]);

  useEffect(() => {
    updateContractorList();
  }, [updateContractorList]);

  const inviteContractor = (user) => {
    sendInviteFromWorkOrderToContractor(
      {
        workOrdersId: [currentWorkOrder._id],
        contractorId: user.userId,
      },
      true,
    )
      .then(() => {
        setInvitedContractors([
          ...invitedContractors,
          user.userId,
        ]);
      });
  };

  const closeModal = () => {
    toggleSecondModalClose('nearByContractorModal', true);
  };

  const handlerWorkedBeforeFilter = () => {
    setIsFilteredByWorkedBefore(!isFilteredByWorkedBefore);
  };

  const handlerPreferred = (newValue, actionMeta) => {
    switch (actionMeta.action) {
      case 'remove-value': {
        newValue = selectedGroups.filter((v) => v.value !== actionMeta.removedValue.value);
        break;
      }
      case 'select-option': {
        const { option } = actionMeta;
        if (option.value === 'all') {
          newValue = preferredGroups.filter((v) => v.value !== 'all');
        }
        break;
      }
      case 'clear':
        newValue = [];
        break;
      default:
        break;
    }

    setSelectedGroups(newValue);
  };

  const handlerSelectAll = (event) => {
    const newSelect = event.target.checked
      ? getAvailableToInviteContractors(contractorList).map((item) => item.userId)
      : [];
    setSelectedContractors(new Set(newSelect));
  };

  const bulkInviteContractors = () => {
    sendInvitesFromWorkOrderToContractors(
      currentWorkOrder._id,
      [...selectedContractors],
      true,
    )
      .then((success) => {
        if (success) {
          setInvitedContractors([
            ...invitedContractors,
            ...selectedContractors,
          ]);
          resetSelect();
        }
      });
  };

  const handlerSingleSelect = (event, userId) => {
    const newSelectList = new Set([...selectedContractors]);
    if (event.target.checked) {
      newSelectList.add(userId);
    } else {
      newSelectList.delete(userId);
    }
    setSelectedContractors(newSelectList);
  };

  if (!modalState?.isOpen) {
    return null;
  }

  const groupsSelect = (
    <div className="nearby-modal-groups dashboard-select-wrap">
      <Select
        isClearable
        isSearchable
        isMulti
        placeholder="Preferred groups"
        options={preferredGroups}
        value={selectedGroups}
        styles={dashboardSelectStyle}
        classNames={dashboardSelectClassNames}
        onChange={handlerPreferred}
      />
    </div>
  );

  return (
    <div
      className="dashboard-modal dashboard-modal--open dashboard-modal--nearby"
      role="presentation"
      onClick={closeModal}
    >
      <div
        className="dashboard-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nearby-contractors-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-modal-header">
          <h4 className="dashboard-modal-title" id="nearby-contractors-title">
            {`Near By Contractor (${contractorList.length})`}
          </h4>
          <button
            type="button"
            className="dashboard-modal-close"
            aria-label="Close nearby contractors"
            onClick={closeModal}
          />
        </div>

        <div className="dashboard-modal-body wurqly-modal-scroll dashboard-modal-form-scroll">
          {!availableApplicantsLoaded ? (
            <div className="nearby-modal-loading">
              <Loader size="lg" />
            </div>
          ) : (
            <>
              {currentWorkOrder && !currentWorkOrder.isRemote ? (
                <div className="nearby-modal-filters">
                  <div className="nearby-modal-range">
                    <span className="nearby-modal-label">Distance</span>
                    <InputRange defaultValue={range} handler={changeRange} />
                  </div>

                  <label htmlFor="isFilteredByWorkedBefore" className="wo-check-btn check_btn dashboard-filter-check nearby-modal-check">
                    <input
                      type="checkbox"
                      id="isFilteredByWorkedBefore"
                      checked={isFilteredByWorkedBefore}
                      onChange={handlerWorkedBeforeFilter}
                    />
                    Worked with before
                  </label>

                  <div className="nearby-modal-groups-row">
                    <span className="nearby-modal-label">Preferred groups</span>
                    {groupsSelect}
                  </div>
                </div>
              ) : null}

              <div className="nearby-modal-toolbar">
                {!viewOnly && (
                  <label htmlFor="nearby-select-all" className="wo-check-btn check_btn dashboard-filter-check nearby-modal-check">
                    <input
                      type="checkbox"
                      id="nearby-select-all"
                      name="nearby-select-all"
                      checked={
                        contractorList.length > 0
                        && selectedContractors.size === fullSelectCount
                      }
                      onChange={handlerSelectAll}
                    />
                    Select All
                  </label>
                )}

                {currentWorkOrder?.isRemote ? groupsSelect : null}

                {!viewOnly && (
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--primary nearby-modal-invite-btn"
                    onClick={bulkInviteContractors}
                    disabled={!selectedContractors?.size}
                  >
                    {`Invite selected (${selectedContractors.size})`}
                  </button>
                )}
              </div>

              <div className="nearby-modal-list">
                {contractorList && contractorList.length > 0 ? (
                  <UserList
                    users={contractorList}
                    handler={inviteContractor}
                    invitedContractors={invitedContractors}
                    viewOnly={viewOnly}
                    profileInfo={(user) => {
                      toggleModal(true, 'contractorDetailsModal', false, { contractorId: user.userId });
                    }}
                    blocked={(user) => {
                      toggleModal(true, 'blockModal', false, { contractorId: user.userId });
                    }}
                    selectedContractors={selectedContractors}
                    handlerCheckbox={handlerSingleSelect}
                  />
                ) : (
                  <p className="nearby-modal-empty">No contractors found nearby.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
  setCurrentApplicants,
  fetchNearByContractors,
  sendInviteFromWorkOrderToContractor,
  sendInvitesFromWorkOrderToContractors,
  toggleSecondModalClose,
  reloadData,
};

const mapStateToProps = (state) => ({
  currentWorkOrder: state.workOrder.currentWorkOrder,
  availableApplicantsLoaded: state.workOrder.availableApplicantsLoaded,
  availableApplicants: state.workOrder.availableApplicants,
  modalState: state.modalState.nearByContractorModal,
  routedApplicants: state.workOrder.routedApplicants,
  userType: state.auth?.user?.userType,
});

export default connect(mapStateToProps, mapDispatchToProps)(NearByContractorModal);
